import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

// Slide 1 — original lifestyle hero (desert car editorial).
const HERO_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/3nna8o3u_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2010_05_05%20PM.png";
// Slide 2 — "Wear the Heritage" editorial (two men on tan leather sofa) with baked-in text.
const HERO_IMAGE_2 =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-jackets-6/artifacts/0lsckyex_ChatGPT%20Image%20Sep%2011%2C%202026%2C%2008_00_49%20PM%20-%20Edited%20-%20Edited%20-%20Edited.png";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Auto-rotate the hero every 4s (Slide 1 ↔ Slide 2).
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s === 0 ? 1 : 0)), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page" className="bg-white text-black">
      {/* ─────────── Auto-rotating 2-slide hero ─────────── */}
      <section
        data-testid="hero-single"
        className="relative w-full h-[70vh] md:h-[92vh] md:min-h-[720px] bg-[#0a0a0a] overflow-hidden"
      >
        {/* Slide 1 — original editorial + text/buttons overlay */}
        <div
          data-testid="hero-slide-1"
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
          style={{ opacity: slide === 0 ? 1 : 0 }}
          aria-hidden={slide !== 0}
        >
          <img
            src={HERO_IMAGE}
            alt=""
            className="absolute inset-0 w-full h-full object-cover kenburns-in"
          />
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
          <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-14 md:pb-20">
            <p data-testid="hero-single-overline" className="text-[11px] md:text-[12px] tracking-[0.28em] uppercase text-white/85">
              Handcrafted Leather
            </p>
            <h1
              data-testid="hero-single-title"
              className="mt-4 font-serif text-[44px] md:text-[72px] leading-[0.98] text-white max-w-[720px]"
            >
              Built by hand.
              <br />
              Worn for a lifetime.
            </h1>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="inline-block bg-[#0a0a0a] text-white text-[11px] tracking-[0.28em] uppercase px-9 py-3.5 hover:bg-[#c9a84c] hover:text-black transition-colors"
              >
                Shop
              </Link>
              <Link
                to="/personalize"
                data-testid="hero-personalize-button"
                className="inline-block border border-white/60 text-white text-[11px] tracking-[0.28em] uppercase px-9 py-3.5 hover:bg-white hover:text-black transition-colors"
              >
                Personalize Yours
              </Link>
            </div>
          </div>
        </div>

        {/* Slide 2 — editorial image with baked-in quotes/graphics, NO text overlay */}
        <div
          data-testid="hero-slide-2"
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out bg-white"
          style={{ opacity: slide === 1 ? 1 : 0 }}
          aria-hidden={slide !== 1}
        >
          <img
            src={HERO_IMAGE_2}
            alt="Boscage — wear the heritage"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </section>

      {/* Guarantee strip */}
      <section
        data-testid="guarantee-strip"
        className="bg-[#c9a84c] text-black py-4 overflow-hidden"
      >
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-2 px-6 text-[11px] tracking-[0.32em] uppercase font-medium">
          <span>Lifetime guarantee</span>
          <span className="opacity-30">◆</span>
          <span>Free North America shipping</span>
          <span className="opacity-30">◆</span>
          <span>14-day pre-order tailoring</span>
        </div>
      </section>

      {/* ─────────── Latest drop — Featured product ─────────── */}
      {products.length > 0 && (
        <section
          data-testid="featured-drop-section"
          className="bg-white py-20 md:py-28"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-10 md:mb-14">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#c9a84c]">
                  Latest drop
                </p>
                <h2 className="font-serif text-3xl md:text-4xl mt-3 text-black leading-tight">
                  The Refined line.
                </h2>
              </div>
              <Link
                to="/shop"
                data-testid="featured-view-all"
                className="hidden sm:inline-block text-[11px] tracking-[0.28em] uppercase text-black border-b border-black pb-1 hover:text-[#c9a84c] hover:border-[#c9a84c] transition-colors"
              >
                View all
              </Link>
            </div>

            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12"
              data-testid="featured-grid"
            >
              {products.map((p) => (
                <FeaturedCard key={p.product_id} p={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────── VIP teaser ─────────── */}
      <section className="bg-white py-24" data-testid="vip-teaser-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="bg-black text-white p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="overline-gold">Private circle</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
                The BOSCAGE VIP &amp; Trade-In.
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
                Subscribe with your name and email — receive your VIP ticket,
                private access to seasonal pre-releases, and our restoration &amp;
                trade-in programme.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <Link
                to="/vip"
                className="btn-vip"
                data-testid="vip-cta-button"
              >
                <span>Become VIP</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─────────── Featured product card (LV-style) ─────────── */
const FeaturedCard = ({ p }) => (
  <Link
    to={`/product/${p.handle}`}
    data-testid={`featured-card-${p.product_id}`}
    className="group block"
  >
    <div className="relative overflow-hidden bg-[#f5f5f5] aspect-[3/4]">
      <img
        src={p.image}
        alt={p.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.2,0.7,0.3,1)] group-hover:scale-[1.04]"
      />
      {p.compare_at_price && (
        <span className="absolute top-3 left-3 text-[9px] tracking-[0.28em] uppercase text-black bg-white px-2 py-1">
          New
        </span>
      )}
    </div>
    <div className="mt-4 px-1">
      <h3 className="text-[13px] text-black leading-tight">{p.title}</h3>
      <p className="text-[12px] text-black/55 mt-0.5">{p.subtitle}</p>
      <div className="mt-2 flex items-baseline gap-2">
        {p.compare_at_price && (
          <span className="text-[12px] text-black/40 line-through">
            ${p.compare_at_price.toFixed(0)}
          </span>
        )}
        <span className="text-[12px] text-black">${p.price.toFixed(0)}</span>
      </div>
    </div>
  </Link>
);

/* ─────────── Represent-style hero panel ─────────── */
const HeroPanel = ({ testId, image, overline, title, cta, imagePosition, motion = "in" }) => (
  <div
    data-testid={testId}
    className="relative overflow-hidden h-[70vh] md:h-[100vh] md:min-h-[720px] bg-[#0a0a0a] group"
  >
    <img
      src={image}
      alt=""
      className={`absolute inset-0 w-full h-full object-cover ${
        motion === "out" ? "kenburns-out" : "kenburns-in"
      }`}
      style={{ objectPosition: imagePosition }}
    />
    {/* subtle bottom gradient for text readability */}
    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />

    <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-12">
      <p
        data-testid={`${testId}-overline`}
        className="text-[11px] md:text-[12px] tracking-[0.22em] text-white/85"
      >
        {overline}
      </p>
      <h1
        data-testid={`${testId}-title`}
        className="mt-3 font-serif text-[42px] md:text-[64px] leading-[1.02] text-white max-w-[520px]"
      >
        {title}
      </h1>
      <div className="mt-6">
        <Link
          to={cta.to}
          data-testid={cta.testId}
          className="inline-block bg-[#0a0a0a] text-white text-[11px] tracking-[0.28em] uppercase px-9 py-3.5 hover:bg-[#c9a84c] hover:text-black transition-colors"
        >
          {cta.label}
        </Link>
      </div>
    </div>
  </div>
);
